import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "@b2b/ui-components/shadcn/ui/Button";
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTitle,
} from "@b2b/ui-components";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Textarea } from "@b2b/ui-components/shadcn/ui/Textarea";
import { Checkbox } from "@b2b/ui-components/shadcn/ui/Checkbox";
import { cn } from "../../lib/utils";
import { getSellerDetailPath } from "../../lib/productRoutes";

export interface SendEnquiryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: {
    id: string;
    name?: string;
    title?: string;
    thumbnailImage?: string;
    images?: string[];
    moq?: number;
    moqUnit?: string;
  };
  sellerName: string;
  /** URL to the seller's profile (e.g. /seller_detail/:sellerId). If provided, "See Profile" link is shown. */
  sellerProfileUrl?: string | null;
  /** Optional seller location/country for display (e.g. "India"). */
  sellerCountry?: string | null;
  /** Optional years in business for display (e.g. 28). */
  sellerYearsInBusiness?: number | null;
  /** Optional expected delivery text (e.g. "Expected Delivery by April 6"). */
  expectedDelivery?: string | null;
  getCurrentUser: () => Promise<{ buyerName: string; buyerEmail: string }>;
  onSubmit: (payload: {
    productId: string;
    subject: string;
    message: string;
    buyerName: string;
    buyerEmail: string;
    buyerPhone?: string;
  }) => Promise<void>;
}

const SendEnquiryModal: React.FC<SendEnquiryModalProps> = ({
  open,
  onOpenChange,
  product,
  sellerName,
  sellerProfileUrl,
  sellerCountry,
  sellerYearsInBusiness,
  expectedDelivery,
  getCurrentUser,
  onSubmit,
}) => {
  const [fromName, setFromName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);

  const productName = product?.name ?? product?.title ?? "Product";
  const productImage =
    product?.thumbnailImage ||
    (Array.isArray(product?.images) && product.images.length > 0
      ? product.images[0]
      : null);
  const moq = product?.moq ?? 1;
  const moqUnit = product?.moqUnit ?? "pieces";
  const minOrderText = `Min. Order: ${moq} ${moqUnit}`;

  useEffect(() => {
    if (open) {
      setMessage("");
      setConsent(false);
      setLoadingUser(true);
      getCurrentUser()
        .then((user) => {
          const name = user.buyerName?.trim() || user.buyerEmail || "—";
          setFromName(name);
          setBuyerEmail(user.buyerEmail || "");
        })
        .catch(() => {
          setFromName("—");
          setBuyerEmail("");
        })
        .finally(() => setLoadingUser(false));
    }
  }, [open, getCurrentUser]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault?.();
    if (!consent || !message?.trim()) return;
    setLoading(true);
    try {
      await onSubmit({
        productId: String(product.id),
        subject: `Product Enquiry - ${productName}`,
        message: message.trim(),
        buyerName: String(fromName ?? ""),
        buyerEmail: String(buyerEmail ?? ""),
      });
      onOpenChange(false);
    } catch (err) {
      // Re-throw so parent can show error toast
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = consent && message.trim().length > 0 && !loading;
  const resolvedSellerProfileUrl = (() => {
    if (!sellerProfileUrl) return null;
    const sellerDetailMatch = sellerProfileUrl.match(/\/seller_detail\/([^/?#]+)/i);
    if (!sellerDetailMatch) return sellerProfileUrl;
    const sellerId = sellerDetailMatch[1];
    return getSellerDetailPath(sellerId);
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay
          className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 duration-300"
          style={{ transitionDuration: "300ms" }}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed z-50 flex h-full w-full max-w-[100vw] flex-col bg-white shadow-xl md:max-w-[50vw]",
            "right-0 top-0 left-auto rounded-l-xl rounded-r-none border border-l border-gray-200 p-0",
            "translate-x-0 transition-transform duration-300 ease-out",
            "data-[state=closed]:translate-x-full",
            "focus:outline-none"
          )}
        >
          {/* Header */}
          <div className="flex shrink-0 items-center justify-center border-b border-gray-100 px-6 py-4 relative">
            <DialogTitle className="text-center text-lg font-semibold text-gray-900">
              Send Enquiry
            </DialogTitle>
            <DialogPrimitive.Close
              className="absolute right-4 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full bg-gray-900 text-white opacity-90 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>

          <form
            className="flex flex-1 flex-col min-h-0"
            onSubmit={(e) => handleSubmit(e)}
          >
          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <div className="space-y-5">
              {/* To: Recipient */}
              <div>
                <p className="mb-2 text-sm font-medium text-gray-500">To:</p>
                <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <span className="font-semibold text-gray-900">
                        {sellerName || "—"}
                      </span>
                      {(sellerCountry || sellerYearsInBusiness != null) && (
                        <p className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500">
                          {sellerCountry && <span>{sellerCountry}</span>}
                          {sellerYearsInBusiness != null && (
                            <span>{sellerYearsInBusiness}yrs</span>
                          )}
                        </p>
                      )}
                    </div>
                    {resolvedSellerProfileUrl && (
                      <a
                        href={resolvedSellerProfileUrl}
                        className="shrink-0 text-sm text-blue-600 hover:underline"
                      >
                        See Profile
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Product info card */}
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <div className="flex gap-3">
                  {productImage && (
                    <img
                      src={productImage}
                      alt=""
                      className="h-16 w-16 shrink-0 rounded border border-gray-200 object-cover"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 line-clamp-2">
                      {productName}
                    </p>
                    <div className="mt-1.5 flex flex-col gap-0.5 text-xs text-gray-500">
                      <span className="text-green-600">In Stock</span>
                      <span>{minOrderText}</span>
                      {expectedDelivery && (
                        <span>{expectedDelivery}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-900">
                  Message:
                </label>
                <Textarea
                  placeholder="Enter your sourcing requirements: product type, material, size, delivery timeline, or certifications."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  className="min-h-[120px] resize-none rounded-md border-gray-300 text-sm"
                />
              </div>

              {/* Terms */}
              <div className="flex items-start gap-2">
                <Checkbox
                  id="consent"
                  checked={consent}
                  onCheckedChange={(checked) => setConsent(checked === true)}
                  className="mt-0.5 shrink-0"
                />
                <p className="text-sm text-gray-600 leading-tight">
                  I agree to the{" "}
                  <span className="font-bold"
                    >Terms and Conditions {" "}
                  </span>
                   and{" "}
                   <span className="font-bold"
                    >Privacy Policy {" "}
                  </span>
                  .
                </p>
              </div>
            </div>
          </div>

          {/* Footer actions */}
          <div className="flex shrink-0 justify-end gap-2 border-t border-gray-100 px-6 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              className="bg-orange-500 text-white hover:bg-orange-600"
            >
              {loading ? "Sending..." : "Send Enquiry"}
            </Button>
          </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};

export { SendEnquiryModal };
